import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LogoUploadModal from "modals/LogoUploadModal";
import CoverUploadModal from "modals/CoverUploadModal";
import config from "../../../config";

function toImgUrl(pathOrUrl, backendBase) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  return `${backendBase}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

const DEFAULT_LOGO_URL = "https://placehold.co/400x400/png?text=Logo";
const DEFAULT_COVER_URL = "https://placehold.co/1200x600/png?text=Cover";
const resolveLogoSrc = (url) => url === DEFAULT_LOGO_URL ? "/assets/images/affiliate-default/affiliate_default_logo.svg" : url;
const resolveCoverSrc = (url) => url === DEFAULT_COVER_URL ? "/assets/images/affiliate-default/affiliate_default_cover.svg" : url;
const isDefaultLogo = (url) => url === DEFAULT_LOGO_URL;

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "#fff" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {right}
        </Stack>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.25)" }} />

        {children}
      </CardContent>
    </Card>
  );
}

export default function SectionImages({
  form,
  setField,
  updatePartial,
  saving,
  dirty,
  onSaved,
  backendBase = "",
  notify,
}) {
  const { t } = useTranslation();
  const [err, setErr] = useState("");
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [coverModalOpen, setCoverModalOpen] = useState(false);

  const token = localStorage.getItem("admin_jwt") || "";
  const coverUrl = resolveCoverSrc(toImgUrl(form.coverImg, backendBase));
  const logoUrl = resolveLogoSrc(toImgUrl(form.logo, backendBase));

  const save = async () => {
    setErr("");
    const ok = await updatePartial(
      {
        logo: form.logo,
        coverImg: form.coverImg,
      },
      "s1"
    );

    if (ok) onSaved?.();
  };

  const handleLogoSaved = (newLogoPath, newLogoPosition) => {
    if (newLogoPath) setField("logo", newLogoPath);
    if (newLogoPosition) setField("logoPosition", newLogoPosition);
    onSaved?.();
  };

  const handleCoverSaved = (newCoverPath) => {
    if (newCoverPath) setField("coverImg", newCoverPath);
    onSaved?.();
  };

  return (
    <>
      <SectionCard
        title={t("info.images.sectionTitle")}
        subtitle={t("info.images.subtitle")}
        right={
          <Button variant="contained" onClick={save} disabled={!dirty || saving}>
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        }
      >
        {/* Outer wrapper — relative but NO overflow:hidden so logo protrudes freely */}
        <Box sx={{ position: "relative", width: "100%" }}>

          {/* Cover — overflow:hidden stays here only */}
          <Box
            sx={{
              width: "100%",
              borderRadius: 3,
              overflow: "hidden",
              position: "relative",
              "&::before": { content: '""', display: "block", paddingTop: "33%" },
              bgcolor: "rgba(0,0,0,0.25)",
              border: "1px solid",
              borderColor: "rgba(255,255,255,0.25)",
            }}
          >
            {coverUrl ? (
              <Box
                component="img"
                src={coverUrl}
                alt="Εξώφυλλο"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(0,0,0,0.35)",
                }}
              >
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  {t("info.images.noCover")}
                </Typography>
              </Box>
            )}

            {/* Camera overlay — same pattern as logo */}
            <Box
              component="button"
              onClick={() => setCoverModalOpen(true)}
              sx={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
                "&:hover": { background: "rgba(0,0,0,0.35)" },
                "& svg": { opacity: 0, transition: "opacity 0.2s" },
                "&:hover svg": { opacity: 1 },
              }}
            >
              <PhotoCameraIcon sx={{ color: "#fff", fontSize: "2.5rem" }} />
            </Box>
          </Box>

          {/* Logo — sibling of cover, half-in/half-out, matches louder-front exactly */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: "12%",
              transform: "translateX(-50%) translateY(50%)",
              width: "15%",
              aspectRatio: "1",
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "common.white",
              bgcolor: "common.white",
              overflow: "hidden",
              boxShadow: "0 6px 28px rgba(0,0,0,0.18)",
            }}
          >
            {isDefaultLogo(toImgUrl(form.logo, backendBase)) ? (
              <>
                <Box component="img" src={logoUrl} alt="Λογότυπο"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <Box sx={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: "#D4AF37" }}>
                    {form.name?.[0] ?? "?"}
                  </Typography>
                </Box>
              </>
            ) : logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Λογότυπο"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: `${form.logoPosition?.x ?? 50}% ${form.logoPosition?.y ?? 50}%`,
                  transform: `scale(${form.logoPosition?.scale ?? 1})`,
                  transformOrigin: `${form.logoPosition?.x ?? 50}% ${form.logoPosition?.y ?? 50}%`,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.12)",
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: "40%", color: "rgba(0,0,0,0.35)" }} />
              </Box>
            )}

            {/* Camera overlay button */}
            <Box
              component="button"
              onClick={() => setLogoModalOpen(true)}
              sx={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
                "&:hover": { background: "rgba(0,0,0,0.35)" },
                "& svg": { opacity: 0, transition: "opacity 0.2s" },
                "&:hover svg": { opacity: 1 },
              }}
            >
              <PhotoCameraIcon sx={{ color: "#fff", fontSize: "35%" }} />
            </Box>
          </Box>
        </Box>

        {/* Spacer — clears the protruding logo half, matches louder-front SPACER_PT */}
        <Box sx={{ paddingTop: "10%" }} />

        <Alert
          severity="info"
          sx={{
            mt: 2,
            bgcolor: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.8)",
            border: "1px solid rgba(255,255,255,0.15)",
            "& .MuiAlert-icon": { color: "rgba(255,255,255,0.6)" },
          }}
        >
          {t("info.images.sizeNote")}
        </Alert>

        {err && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
      </SectionCard>

      <LogoUploadModal
        open={logoModalOpen}
        onClose={() => setLogoModalOpen(false)}
        onSaved={handleLogoSaved}
        currentCoverUrl={form.coverImg}
        backendBase={backendBase || config.BACKEND_BASE_URL}
        token={token}
        notify={notify}
      />

      <CoverUploadModal
        open={coverModalOpen}
        onClose={() => setCoverModalOpen(false)}
        onSaved={handleCoverSaved}
        currentCoverUrl={form.coverImg}
        logoUrl={logoUrl}
        logoPosition={form.logoPosition}
        backendBase={backendBase || config.BACKEND_BASE_URL}
        token={token}
        notify={notify}
      />
    </>
  );
}